import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Spinner, Text, makeStyles } from "@fluentui/react-components";
import apiServices from "../service";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    gap: "16px",
  },
});

/**
 * Keycloak's redirect target for the Assyst MCP OAuth flow (registered as
 * this Keycloak client's redirect_uri — see MCP_UI_CALLBACK_URL in
 * middleware's .env). Reads the code/iss/state Keycloak appends, POSTs them
 * to /asc-auth/handle-code, then navigates the browser to whatever
 * redirectUrl that returns (the AI client's own listener).
 */
const McpCallbackPage: React.FC = () => {
  const styles = useStyles();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("Completing sign-in…");

  useEffect(() => {
    const code = searchParams.get("code");
    const iss = searchParams.get("iss");
    const state = searchParams.get("state");
    const error = searchParams.get("error");

    if (error) {
      setStatus("error");
      setMessage(`Authorisation denied: ${error}`);
      return;
    }

    if (!code || !iss || !state) {
      setStatus("error");
      setMessage("Missing code, issuer, or state from the identity provider.");
      return;
    }

    apiServices.auth
      .mcpHandleCode({ code, iss, state })
      .then((response) => {
        const redirectUrl = response.data?.redirectUrl;
        if (!redirectUrl) {
          setStatus("error");
          setMessage("No redirect URL returned — cannot continue.");
          return;
        }
        setStatus("success");
        setMessage("Redirecting you back…");
        window.location.href = redirectUrl;
      })
      .catch((err: any) => {
        setStatus("error");
        const detail =
          err?.response?.data?.error || err?.message || "Unknown error";
        setMessage(`Failed to complete sign-in: ${detail}`);
      });
  }, [searchParams]);

  return (
    <div className={styles.container}>
      {status === "loading" && <Spinner size="large" label={message} />}
      {status === "success" && (
        <Text size={500} weight="semibold" style={{ color: "#34a853" }}>
          ✓ {message}
        </Text>
      )}
      {status === "error" && (
        <Text size={400} style={{ color: "#d13438" }}>
          {message}
        </Text>
      )}
    </div>
  );
};

export default McpCallbackPage;
